import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { templatesApi, getDocTypeLabel, type DocType } from "../api.js";
import { tenantProfessionsApi } from "../api/tenantProfessionsApi.js";
import { BlockList, type AddBlockKind } from "../components/editor/BlockList.js";
import { CustomItemPicker } from "../components/editor/CustomItemPicker.js";
import { IssuerMasterDialog } from "../components/editor/IssuerMasterDialog.js";
import { GridEditor } from "../components/editor/GridEditor.js";
import { PreviewPane } from "../components/preview/PreviewPane.js";
import { Button } from "../components/ui/Button.js";
import { evaluateGridValues } from "../hooks/useFormulaEval.js";
import { useLivePreview } from "../hooks/useLivePreview.js";
import { useAIStore } from "../stores/aiStore.js";
import { useEditorStore } from "../stores/editorStore.js";
import { createBlankTemplate } from "../lib/createBlankTemplate.js";
import { collectIssuerRefs, issuerRefLabel, resolveIssuerRef } from "../lib/issuerMaster.js";
import { useDocTypeProfession } from "../hooks/useDocTypeProfession.js";
import type { AnyBlock, ComputedValueBlock, GridBlock, RowContainerBlock, SettlementBlock, TemplateDef } from "@juro/shared";
import { toast } from "../components/Toast.js";
import { useScreenView, consumeTemplateDraftOrigin } from "../lib/analytics.js";

type BlockAlign = "left" | "center" | "right";

function createDetailBlock(id: string): GridBlock {
  const blank = createBlankTemplate("明細");
  const grid = blank.body.blocks.find((block): block is GridBlock => block.type === "grid");
  if (!grid) throw new Error("blank grid template not found");
  return {
    ...grid,
    id,
    label: "明細",
    summary: [
      { id: "summary_subtotal", label: "小計", cells: { amount: "=sum(rows[*.subtotal].amount)" } },
      { id: "summary_tax", label: "消費税", cells: { amount: "=consumption_tax({summary_subtotal.amount})" } },
      { id: "summary_total", label: "合計", emphasis: true, cells: { amount: "={summary_subtotal.amount} + {summary_tax.amount}" } },
    ],
  };
}

function createBlock(kind: AddBlockKind): AnyBlock {
  const suffix = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
  switch (kind) {
    case "title":
      return { id: `blk_title_${suffix}`, label: "タイトル", type: "title", text: "タイトル", style: { font_size: 18, align: "center", bold: true } };
    case "recipient":
      return { id: `blk_recipient_${suffix}`, label: "宛名", type: "kv_block", orientation: "horizontal", items: [{ label: "宛名", value: "{{client.name}}", data_key: "client.name", input_type: "text" }] };
    case "office":
      return { id: `blk_office_${suffix}`, label: "会社名", type: "kv_block", orientation: "horizontal", items: [{ label: "会社名", value: "登記測量 長坂・天野合同事務所", data_key: "office.name", input_type: "text" }] };
    case "registration":
      return { id: `blk_registration_${suffix}`, label: "士業登録番号", type: "kv_block", orientation: "horizontal", items: [{ label: "登録番号", value: "{{issuer.registration_number}}", data_key: "issuer.registration_number", input_type: "text" }] };
    case "invoice_registration":
      return { id: `blk_invoice_registration_${suffix}`, label: "適格請求書発行事業者番号", type: "kv_block", orientation: "horizontal", items: [{ label: "適格請求書発行事業者番号", value: "{{invoice.registration_number}}", data_key: "invoice.registration_number", input_type: "text" }] };
    case "issuer_name":
      return { id: `blk_issuer_name_${suffix}`, label: "資格者氏名", type: "kv_block", orientation: "horizontal", items: [{ label: "資格者", value: "{{issuer.name}}", data_key: "issuer.name", input_type: "text" }] };
    case "issuer_qualification":
      return { id: `blk_issuer_qualification_${suffix}`, label: "資格名", type: "kv_block", orientation: "horizontal", items: [{ label: "資格名", value: "{{issuer.qualification}}", data_key: "issuer.qualification", input_type: "text" }] };
    case "issuer_business":
      return { id: `blk_issuer_business_${suffix}`, label: "業務表示", type: "kv_block", orientation: "horizontal", items: [{ label: "業務表示", value: "{{issuer.business_label}}", data_key: "issuer.business_label", input_type: "text" }] };
    case "issuer_custom":
      return { id: `blk_issuer_custom_${suffix}`, label: "資格者カスタム項目", type: "kv_block", orientation: "horizontal", items: [{ label: "カスタム項目", value: "{{issuer.rules.field_key}}", data_key: "issuer.rules.field_key", input_type: "text" }] };
    case "transactionDate":
      return { id: `blk_transaction_date_${suffix}`, label: "取引年月", type: "kv_block", orientation: "horizontal", items: [{ label: "取引年月", value: "{{transaction.date}}", data_key: "transaction.date", input_type: "date" }] };
    case "invoiceAmount":
      return { id: `blk_invoice_amount_${suffix}`, label: "請求額", type: "computed_value", formula: "=detail_total()", format: "currency", data_key: "amount.invoice", style: { font_size: 14, align: "right", bold: true } };
    case "text":
      return { id: `blk_text_${suffix}`, label: "テキスト", type: "text", text: "テキスト", style: { font_size: 10 } };
    case "detail":
      return createDetailBlock(`grid_${suffix}`);
    case "row_container": {
      const rc: RowContainerBlock = {
        id: `blk_row_${suffix}`,
        type: "row_container",
        columns: [
          { width: 50, align: "left", blocks: [] },
          { width: 50, align: "right", blocks: [] },
        ],
      };
      return rc;
    }
    case "settlement": {
      const s: SettlementBlock = {
        id: `blk_settlement_${suffix}`,
        type: "settlement",
        bordered: true,
        rows: [
          { label: "合計①+②", formula_text: "①+②", ref: "③", value_key: "computed.gross" },
          { label: "消費税", formula_text: "①×10%", ref: "④", value_key: "computed.consumption_tax" },
          { label: "源泉所得税", formula_text: "(①−10,000)×10.21%", ref: "⑤", value_key: "computed.withholding_tax" },
          { label: "差引受領額", formula_text: "③+④−⑤", value_key: "computed.total" },
        ],
      };
      return s;
    }
  }
}

function toNumber(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") return Number(value.replace(/,/g, "")) || 0;
  return 0;
}

function computeDetailSubtotal(template: TemplateDef): number {
  return template.body.blocks.reduce((sum, block) => {
    if (block.type !== "grid") return sum;
    const rows = evaluateGridValues(block);
    return sum + Object.entries(rows).reduce((rowSum, [rowId, row]) => {
      const definition = block.rows.find((item) => item.id === rowId);
      return definition?.type === "subtotal" ? rowSum + toNumber(row.amount) : rowSum;
    }, 0);
  }, 0);
}

function evaluateComputedBlock(block: ComputedValueBlock, detailSubtotal: number): number {
  const tax = Math.floor(detailSubtotal * 0.1);
  if (block.formula.includes("detail_total")) return detailSubtotal + tax;
  if (block.formula.includes("consumption_tax")) return detailSubtotal + tax;
  if (block.formula.includes("detail_subtotal")) return detailSubtotal;
  return 0;
}

function formatComputed(value: number, format: ComputedValueBlock["format"]): string {
  const rounded = Math.trunc(value);
  return format === "currency" ? rounded.toLocaleString("ja-JP") : String(rounded);
}

export function TemplateEditPage() {
  useScreenView("template_edit");
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const docType = (searchParams.get("type") ?? "judicial_invoice") as DocType;
  const draft = useEditorStore((state) => state.draft);
  const templateId = useEditorStore((state) => state.templateId);
  const isDirty = useEditorStore((state) => state.isDirty);
  const selectedBlockId = useEditorStore((state) => state.selectedBlockId);
  const loadDraft = useEditorStore((state) => state.loadDraft);
  const setDraft = useEditorStore((state) => state.setDraft);
  const selectBlock = useEditorStore((state) => state.selectBlock);
  const markSaved = useEditorStore((state) => state.markSaved);
  const undo = useEditorStore((state) => state.undo);
  const redo = useEditorStore((state) => state.redo);
  const openAI = useAIStore((state) => state.openPanel);
  const [showPreview, setShowPreview] = useState(true);
  const [showIssuerDialog, setShowIssuerDialog] = useState(false);
  const [showCustomItemPicker, setShowCustomItemPicker] = useState(false);
  const isDraftRoute = !id || id === "draft" || id === "new";

  useLivePreview(draft);

  const { data: existing, isLoading } = useQuery({
    queryKey: ["template", id],
    queryFn: () => templatesApi.get(id!),
    enabled: !isDraftRoute && !!id,
  });

  useEffect(() => {
    if (existing) {
      if (draft && templateId === existing.id) return;
      const template = existing.template_json ?? createBlankTemplate(existing.name);
      loadDraft(existing.id, template);
    }
  }, [draft, existing, loadDraft, templateId]);

  useEffect(() => {
    if (!draft && isDraftRoute) {
      loadDraft(null, createBlankTemplate(`${getDocTypeLabel(docType)}テンプレート`));
    }
  }, [docType, draft, isDraftRoute, loadDraft]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error("保存するテンプレートがありません");
      if (templateId) {
        return templatesApi.update(templateId, { name: draft.name ?? "V2テンプレート", template_json: draft, schema_version: 2 });
      }
      return templatesApi.create({ doc_type: docType, name: draft.name ?? `${getDocTypeLabel(docType)}テンプレート`, description: "V2 grid template", template_json: draft, schema_version: 2, created_via: consumeTemplateDraftOrigin() ?? "blank" });
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      if (saved.template_json) markSaved(saved.template_json);
      toast("テンプレートを保存しました");
      if (!templateId) navigate(`/settings/templates/${saved.id}?type=${docType}`, { replace: true });
    },
    onError: (error) => toast(error instanceof Error ? error.message : String(error), "error"),
  });

  const detailSubtotal = useMemo(() => draft ? computeDetailSubtotal(draft) : 0, [draft]);

  // このテンプレートが参照する発行者情報（業務マスタ）。未入力があればバナーで登録を促す
  const { profession: issuerProfessionKey } = useDocTypeProfession(docType);
  const issuerRefs = useMemo(() => (draft ? collectIssuerRefs(draft) : []), [draft]);
  const { data: professionsData } = useQuery({
    queryKey: ["tenant-professions"],
    queryFn: () => tenantProfessionsApi.list(),
    enabled: !!issuerProfessionKey && issuerRefs.length > 0,
  });
  const issuerProfession = professionsData?.data.find((entry) => entry.profession === issuerProfessionKey) ?? null;
  const missingIssuerRefs = useMemo(
    () => (issuerProfession ? issuerRefs.filter((ref) => !resolveIssuerRef(issuerProfession, ref)) : []),
    [issuerProfession, issuerRefs],
  );

  if (isLoading || !draft) return <div style={{ padding: 24, color: "#64748b" }}>読み込み中...</div>;

  const selectedBlock = draft.body.blocks.find((block) => block.id === selectedBlockId) ?? draft.body.blocks.find((block) => block.type === "grid") ?? null;

  function insertBlock(block: AnyBlock) {
    setDraft((template) => ({ ...template, body: { blocks: [...template.body.blocks, block] } }));
    selectBlock(block.id);
  }

  function handleAddBlock(kind: AddBlockKind) {
    if (kind === "issuer_custom" && issuerProfessionKey) {
      setShowCustomItemPicker(true);
      return;
    }
    insertBlock(createBlock(kind));
  }

  function reorderBlocks(blocks: AnyBlock[], draggedId: string, targetId: string): AnyBlock[] {
    const from = blocks.findIndex((block) => block.id === draggedId);
    const to = blocks.findIndex((block) => block.id === targetId);
    if (from < 0 || to < 0 || from === to) return blocks;
    const next = [...blocks];
    const [item] = next.splice(from, 1);
    if (!item) return blocks;
    next.splice(to, 0, item);
    return next;
  }

  function handleMoveBlock(id: string, direction: "up" | "down") {
    setDraft((template) => {
      const index = template.body.blocks.findIndex((block) => block.id === id);
      const nextIndex = direction === "up" ? index - 1 : index + 1;
      if (index < 0 || nextIndex < 0 || nextIndex >= template.body.blocks.length) return template;
      return { ...template, body: { blocks: reorderBlocks(template.body.blocks, id, template.body.blocks[nextIndex]?.id ?? id) } };
    });
  }

  function handleReorderBlock(draggedId: string, targetId: string) {
    setDraft((template) => ({ ...template, body: { blocks: reorderBlocks(template.body.blocks, draggedId, targetId) } }));
  }

  function handleDeleteBlock(id: string) {
    if (!draft) return;

    const index = draft.body.blocks.findIndex((block) => block.id === id);
    if (index < 0) return;

    const nextBlocks = draft.body.blocks.filter((block) => block.id !== id);
    const nextSelectedBlockId = nextBlocks[index]?.id ?? nextBlocks[index - 1]?.id ?? null;

    setDraft((template) => ({ ...template, body: { blocks: template.body.blocks.filter((block) => block.id !== id) } }));
    selectBlock(nextSelectedBlockId);
  }

  function updateSelectedBlock(fn: (block: AnyBlock) => AnyBlock) {
    if (!selectedBlock) return;
    setDraft((template) => ({
      ...template,
      body: { blocks: template.body.blocks.map((block) => block.id === selectedBlock.id ? fn(block) : block) },
    }));
  }

  function updateSelectedAlign(align: BlockAlign) {
    updateSelectedBlock((block) => ({ ...block, style: { ...(block.style ?? {}), align } }));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", background: "#f8fafc" }}>
      <header style={{ height: 58, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px", borderBottom: "1px solid #e2e8f0", background: "#fff", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <Link to="/settings/templates" style={{ color: "#0f766e", fontSize: 13, fontWeight: 800, textDecoration: "none" }}>テンプレート管理</Link>
          <span style={{ color: "#cbd5e1" }}>/</span>
          <input value={draft.name ?? ""} placeholder="テンプレート名" onChange={(event) => setDraft((template) => ({ ...template, name: event.target.value }))} style={{ border: "none", outline: "none", fontSize: 16, fontWeight: 900, color: "#0f172a", width: 260, background: "transparent" }} />
          <span style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>{getDocTypeLabel(docType)}</span>
          {isDirty && <span style={{ color: "#d97706", fontSize: 12, fontWeight: 800 }}>未保存</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button onClick={undo}>元に戻す</Button>
          <Button onClick={redo}>やり直し</Button>
          {issuerProfessionKey && issuerRefs.length > 0 && (
            <Button onClick={() => setShowIssuerDialog(true)}>発行者情報</Button>
          )}
          <Button onClick={openAI}>AI修正</Button>
          <Button variant="primary" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>{saveMutation.isPending ? "保存中..." : "保存"}</Button>
        </div>
      </header>

      {issuerProfessionKey && missingIssuerRefs.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 18px", background: "#fff7ed", borderBottom: "1px solid #fed7aa", flexShrink: 0 }}>
          <div style={{ fontSize: 13, color: "#c2410c", fontWeight: 700, minWidth: 0 }}>
            このテンプレートは{issuerProfession?.qualification_label ?? issuerProfessionKey}の発行者情報を参照していますが、未入力の項目があります:
            {" "}{missingIssuerRefs.map((ref) => issuerRefLabel(ref, issuerProfession)).join("、")}
          </div>
          <Button onClick={() => setShowIssuerDialog(true)} style={{ flexShrink: 0 }}>今すぐ登録</Button>
        </div>
      )}

      {showIssuerDialog && issuerProfessionKey && (
        <IssuerMasterDialog docType={docType} referencedKeys={issuerRefs} onClose={() => setShowIssuerDialog(false)} />
      )}

      {showCustomItemPicker && (
        <CustomItemPicker docType={docType} onPick={insertBlock} onClose={() => setShowCustomItemPicker(false)} />
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 0, overflowX: "auto" }}>
        <BlockList blocks={draft.body.blocks} onAddBlock={handleAddBlock} onMoveBlock={handleMoveBlock} onReorderBlock={handleReorderBlock} onDeleteBlock={handleDeleteBlock} />
        <main style={{ flex: 1, minWidth: 0, overflow: "auto", padding: 16 }}>
          {selectedBlock && (
            <section style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>ブロック配置</div>
                <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedBlock.label ?? selectedBlock.id}</div>
              </div>
              <div role="group" aria-label="ブロックの横位置" style={{ display: "flex", gap: 6 }}>
                {([
                  ["left", "左"],
                  ["center", "中央"],
                  ["right", "右"],
                ] as const).map(([value, label]) => {
                  const active = (selectedBlock.style?.align ?? (selectedBlock.type === "title" ? "center" : "left")) === value;
                  return (
                    <Button
                      key={value}
                      variant={active ? "primary" : "outline"}
                      onClick={() => updateSelectedAlign(value)}
                      aria-pressed={active}
                      style={{ minWidth: 52 }}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            </section>
          )}
          {selectedBlock?.type === "grid" ? (
            <GridEditor block={selectedBlock} />
          ) : selectedBlock ? (
            <section style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 13, color: "#64748b", fontWeight: 800, marginBottom: 8 }}>{selectedBlock.type === "computed_value" ? "計算ブロック" : selectedBlock.type}</div>
              {"text" in selectedBlock && (
                <textarea value={String(selectedBlock.text)} placeholder="本文を入力" onChange={(event) => updateSelectedBlock((block) => "text" in block ? { ...block, text: event.target.value } : block)} style={{ width: "100%", minHeight: 140, border: "1px solid #cbd5e1", borderRadius: 8, padding: 10, fontSize: 13, boxSizing: "border-box" }} />
              )}
              {selectedBlock.type === "kv_block" && (
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>ドキュメント作成時は「入力キー」に対応する値を入力して、このテンプレートへ差し込みます。</div>
                  {selectedBlock.items.map((item, index) => (
                    <div key={index} style={{ display: "grid", gridTemplateColumns: "110px 1fr 150px", gap: 8 }}>
                      <input value={item.label} placeholder="表示名" onChange={(event) => updateSelectedBlock((block) => block.type === "kv_block" ? { ...block, items: block.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, label: event.target.value } : entry) } : block)} style={{ height: 32, border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 10px", fontSize: 13 }} />
                      <input value={String(item.value)} placeholder="テンプレート表示値または {{入力キー}}" onChange={(event) => updateSelectedBlock((block) => block.type === "kv_block" ? { ...block, items: block.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, value: event.target.value } : entry) } : block)} style={{ height: 32, border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 10px", fontSize: 13 }} />
                      <input value={item.data_key ?? ""} placeholder="入力キー" onChange={(event) => updateSelectedBlock((block) => block.type === "kv_block" ? { ...block, items: block.items.map((entry, itemIndex) => itemIndex === index ? { ...entry, data_key: event.target.value } : entry) } : block)} style={{ height: 32, border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 10px", fontSize: 13 }} />
                    </div>
                  ))}
                </div>
              )}
              {selectedBlock.type === "computed_value" && (
                <div style={{ display: "grid", gap: 10, maxWidth: 720 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", alignItems: "center", gap: 8 }}>
                    <label style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>表示名</label>
                    <input value={selectedBlock.label} placeholder="表示名" onChange={(event) => updateSelectedBlock((block) => block.type === "computed_value" ? { ...block, label: event.target.value } : block)} style={{ height: 32, border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 10px", fontSize: 13 }} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", alignItems: "center", gap: 8 }}>
                    <label style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>計算式</label>
                    <input value={selectedBlock.formula} placeholder="=detail_total()" onChange={(event) => updateSelectedBlock((block) => block.type === "computed_value" ? { ...block, formula: event.target.value } : block)} style={{ height: 32, border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 10px", fontSize: 13, fontFamily: "monospace" }} />
                  </div>
                  <div style={{ padding: 12, borderRadius: 8, border: "1px solid #dbe3ea", background: "#f8fafc", fontSize: 13, color: "#334155" }}>
                    現在の明細からの試算: <strong>{selectedBlock.label} {formatComputed(evaluateComputedBlock(selectedBlock, detailSubtotal), selectedBlock.format)}</strong>
                  </div>
                </div>
              )}
              {selectedBlock.type === "row_container" && (
                <div style={{ display: "grid", gap: 10, maxWidth: 720 }}>
                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, padding: "8px 0" }}>
                    2カラムレイアウト。各カラムにブロックを配置するにはJSONを直接編集するか、AIアシストをご利用ください。
                  </div>
                  {selectedBlock.columns.map((col, idx) => (
                    <div key={idx} style={{ display: "grid", gridTemplateColumns: "120px 1fr", alignItems: "center", gap: 8 }}>
                      <label style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>カラム{idx + 1} 幅(%)</label>
                      <input
                        type="number" min={10} max={90}
                        value={col.width ?? 50}
                        onChange={(event) => updateSelectedBlock((block) => {
                          if (block.type !== "row_container") return block;
                          const cols = block.columns.map((c, i) => i === idx ? { ...c, width: Number(event.target.value) } : c);
                          return { ...block, columns: cols };
                        })}
                        style={{ height: 32, border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 10px", fontSize: 13, width: 80 }}
                      />
                    </div>
                  ))}
                </div>
              )}
              {selectedBlock.type === "settlement" && (
                <div style={{ display: "grid", gap: 10, maxWidth: 720 }}>
                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>合計欄の行一覧</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {selectedBlock.rows.map((row, idx) => (
                      <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 140px 60px 160px", gap: 6, alignItems: "center" }}>
                        <input
                          value={row.label}
                          placeholder="ラベル"
                          onChange={(event) => updateSelectedBlock((block) => {
                            if (block.type !== "settlement") return block;
                            const rows = block.rows.map((r, i) => i === idx ? { ...r, label: event.target.value } : r);
                            return { ...block, rows };
                          })}
                          style={{ height: 30, border: "1px solid #cbd5e1", borderRadius: 6, padding: "0 8px", fontSize: 12 }}
                        />
                        <input
                          value={row.formula_text ?? ""}
                          placeholder="計算式表示"
                          onChange={(event) => updateSelectedBlock((block) => {
                            if (block.type !== "settlement") return block;
                            const rows = block.rows.map((r, i) => i === idx ? { ...r, formula_text: event.target.value } : r);
                            return { ...block, rows };
                          })}
                          style={{ height: 30, border: "1px solid #cbd5e1", borderRadius: 6, padding: "0 8px", fontSize: 12 }}
                        />
                        <input
                          value={row.ref ?? ""}
                          placeholder="記号"
                          onChange={(event) => updateSelectedBlock((block) => {
                            if (block.type !== "settlement") return block;
                            const rows = block.rows.map((r, i) => i === idx ? { ...r, ref: event.target.value || undefined } : r);
                            return { ...block, rows };
                          })}
                          style={{ height: 30, border: "1px solid #cbd5e1", borderRadius: 6, padding: "0 8px", fontSize: 12 }}
                        />
                        <input
                          value={row.value_key ?? ""}
                          placeholder="computed.total"
                          onChange={(event) => updateSelectedBlock((block) => {
                            if (block.type !== "settlement") return block;
                            const rows = block.rows.map((r, i) => i === idx ? { ...r, value_key: event.target.value } : r);
                            return { ...block, rows };
                          })}
                          style={{ height: 30, border: "1px solid #cbd5e1", borderRadius: 6, padding: "0 8px", fontSize: 12 }}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <label style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>枠線</label>
                    <input
                      type="checkbox"
                      checked={!!selectedBlock.bordered}
                      onChange={(event) => updateSelectedBlock((block) => block.type === "settlement" ? { ...block, bordered: event.target.checked } : block)}
                    />
                    <label style={{ fontSize: 12, color: "#64748b", fontWeight: 800, marginLeft: 12 }}>縦ラベル</label>
                    <input
                      value={selectedBlock.vertical_label ?? ""}
                      placeholder="合計"
                      onChange={(event) => updateSelectedBlock((block) => block.type === "settlement" ? { ...block, vertical_label: event.target.value || undefined } : block)}
                      style={{ height: 30, border: "1px solid #cbd5e1", borderRadius: 6, padding: "0 8px", fontSize: 12, width: 80 }}
                    />
                  </div>
                </div>
              )}
            </section>
          ) : (
            <div style={{ color: "#94a3b8", textAlign: "center", paddingTop: 80 }}>ブロックを選択してください。</div>
          )}
        </main>
        {showPreview ? (
          <PreviewPane onHide={() => setShowPreview(false)} />
        ) : (
          <aside style={{ width: 44, flexShrink: 0, borderLeft: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 12 }}>
            <Button aria-label="PDFプレビューを表示" onClick={() => setShowPreview(true)} style={{ width: 32, minHeight: 32, padding: 0 }}>◀</Button>
          </aside>
        )}
      </div>
    </div>
  );
}
